import { isCatalogItemType } from "building-types/catalogItemTools";
import type { Doc } from "building-types/doc";
import type { CatalogItemType } from "building-types/doc/catalogTypes";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { docEntityTypesContainingForeignKeys } from "building-types/doc/docEntityTypeToForeignKeyRelations";
import {
  docEntityTypesInHashTables,
  docHashJoinTableFieldToHashEntityTypes,
  docHashJoinTableFieldToValueEntityType,
} from "building-types/doc/docHashTableFieldsToForeignKeyRelations";
import { isDocEntityType } from "building-types/handles/docEntityHandleTypeTools";
import type { Formula } from "building-types/options/formula";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import objectHasOwnField from "common/utils/objectHasOwnField";
import { objectTypedKeys } from "common/utils/objectTypedKeys";
import { formulaOptionHandles, removeIdentifierPrefix } from "../formula/formulaConditions";
import { formulaGetIdentifiers, isFormula } from "../formula/formulaParsing";
import { DEFAULT_IDENTIFIER_PREFIX } from "../formula/formulaUtils";
import { isOptionId, isOptionSetId } from "../options/optionTools";
import { SystemOptionSet } from "../options/SystemOptionSetEnum";
import { collectParameterIdsInString, parameterTypes, stringContainsParameterTokens } from "../parameterTools";
import { resolveSchemaForEntity } from "../schema/docSchemaTools";
import type { DocEntity } from "./docEntity";
import { isDocEntityBase } from "./docEntityBaseTools";
import type { FkFieldDescriptor, NestedValueDescriptorGroup } from "./docEntityFkFieldDescriptors";
import { getFkFieldDescriptors } from "./docEntityFkFieldDescriptors";
import type { DocEntityNode } from "./docEntityNode";
import type { DocEntityReference } from "./docEntityReferenceTypes";

/** True when `reference` resolves to the entity identified by `entityType`/`entityId`. */
export function docEntityReferenceTargetsEntity(
  reference: DocEntityReference,
  entityType: DocEntityType,
  entityId: string | undefined,
): boolean {
  if (!entityId) {
    return false;
  }

  const isTypeMatch = Array.isArray(reference.referencedEntityType)
    ? reference.referencedEntityType.includes(entityType)
    : reference.referencedEntityType === entityType;

  return isTypeMatch && reference.referencedEntityId === entityId;
}

/**
 * Extracts foreign key references from an entity by reading field values at
 * pre-computed field paths derived from the entity's schema.
 *
 * Instead of running SchemaValidator.validate() to traverse the entire schema tree,
 * this function uses {@link getFkFieldDescriptors} to get a cached list of FK field
 * descriptors for the entity's schema, then reads values directly from the entity.
 */
export function docEntityNodeExtractForeignKeyReferences(node: DocEntityNode): DocEntityReference[] {
  const foreignKeyReferences: DocEntityReference[] = [];

  const entity = node.getDocEntity();

  if (!entity) {
    return foreignKeyReferences;
  }

  const rawSchema = node.getDocSchema();

  if (!rawSchema || !docEntityTypesContainingForeignKeys[node.getEntityType()]) {
    return foreignKeyReferences;
  }

  const schema = resolveSchemaForEntity(rawSchema, entity as Record<string, unknown>);
  const descriptors = getFkFieldDescriptors(schema);

  if (descriptors.length === 0) {
    return foreignKeyReferences;
  }

  const referenceMap = new Map<string, DocEntityReference>();

  for (const descriptor of descriptors) {
    applyDescriptor(entity, entity, descriptor, referenceMap, foreignKeyReferences);
  }

  return foreignKeyReferences;
}

/**
 * Extracts forward references from Doc-level hash join tables (fkHashJoinTable() fields like
 * entityGroups, entityRoomLayouts, componentMembers) for a given entity.
 *
 * Only iterates {@link docHashJoinTableFieldToHashEntityTypes} — self-referential hash tables
 * (locked, hidden) are excluded by schema kind and don't appear in the join table mappings.
 *
 * The reverse direction (e.g., GROUP seeing its member SETTINGS_SETs) is handled by
 * {@link DocEntityNodeBase.getReverseReferences} via the hash join table reverse reference
 * candidate types folded into {@link docEntityTypeToReverseReferenceTypes} at code-gen time.
 */
/**
 * Inverted lookup: entity type → Doc hash-join table field paths where that type
 * can appear as a hashed key. Built once from the generated forward map.
 */
const hashJoinFieldPathsByEntityType: { [type in DocEntityType]?: readonly string[] } = (() => {
  const result: { [type in DocEntityType]?: string[] } = {};

  for (const fieldPath in docHashJoinTableFieldToHashEntityTypes) {
    const hashEntityTypes = docHashJoinTableFieldToHashEntityTypes[fieldPath as keyof Doc];
    if (!hashEntityTypes) {
      continue;
    }

    for (const type of hashEntityTypes) {
      const paths = result[type];
      if (paths) {
        paths.push(fieldPath);
      } else {
        result[type] = [fieldPath];
      }
    }
  }

  return result;
})();

export function extractDocHashTableReferences(
  doc: Doc,
  entityType: DocEntityType,
  handleHash: string,
): DocEntityReference[] {
  if (!docEntityTypesInHashTables[entityType]) {
    return [];
  }

  const fieldPaths = hashJoinFieldPathsByEntityType[entityType];
  if (!fieldPaths || fieldPaths.length === 0) {
    return [];
  }

  const references: DocEntityReference[] = [];

  for (const fieldPath of fieldPaths) {
    const hashTable = doc[fieldPath as keyof Doc];

    if (!hashTable || typeof hashTable !== "object" || Array.isArray(hashTable)) {
      continue;
    }

    const value = (hashTable as Record<string, unknown>)[handleHash];

    if (typeof value !== "string" || value === "" || value === "undefined") {
      continue;
    }

    const valueEntityType = docHashJoinTableFieldToValueEntityType[fieldPath as keyof Doc];

    if (!valueEntityType || !isDocEntityType(valueEntityType)) {
      continue;
    }

    references.push({
      fieldPath,
      referencedEntityType: valueEntityType,
      referencedEntityId: value,
      entityType,
    });
  }

  return references;
}

export const referenceableDocEntityTypes = objectTypedKeys(docEntityTypesContainingForeignKeys).filter(
  (type) => docEntityTypesContainingForeignKeys[type],
);

/**
 * Reads a nested field value using pre-split path segments from an FK descriptor.
 * An empty parts array returns the object itself (root-level FK values in containers).
 */
function readFieldValueByParts(obj: any, parts: readonly string[]): any {
  let current = obj;
  for (const key of parts) {
    if (current === null || current === undefined || typeof current !== "object") {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value !== "" && value !== "undefined";
}

function createReference(
  fieldPath: string,
  referencedEntityType: DocEntityType,
  referencedEntityId: string | undefined,
  entity: DocEntity,
): DocEntityReference {
  const ref: DocEntityReference = {
    fieldPath,
    referencedEntityType,
    referencedEntityId,
  };

  if (isDocEntityBase(entity)) {
    Object.assign(ref, { entityType: entity.entityType });

    if ("catalogItemType" in entity && isCatalogItemType(entity.catalogItemType)) {
      Object.assign(ref, { catalogItemType: entity.catalogItemType });
    }

    if (typeof entity.id === "string") {
      Object.assign(ref, { entityId: entity.id });
    }
  }

  return ref;
}

/**
 * Adds a reference to the output if it hasn't been seen yet.
 * Deduplicates by fieldPath + type + id so distinct FK fields that point to
 * the same entity id are preserved.
 */
function addDeduplicatedReference(
  fieldPath: string,
  entity: DocEntity,
  entityType: DocEntityType,
  entityId: string | undefined,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  if (typeof entityId !== "string") {
    return;
  }

  const referenceKey = `${fieldPath}:${entityType}:${entityId}`;

  if (!referenceMap.has(referenceKey)) {
    const newReference = createReference(fieldPath, entityType, entityId, entity);
    referenceMap.set(referenceKey, newReference);
    foreignKeyReferences.push(newReference);
  }
}

/**
 * Applies a single descriptor to the entity, dispatching by fkKind.
 * When called from a nested context (array element or table value), parentFieldPath
 * provides the container field's path so references carry the correct top-level field name.
 */
function applyDescriptor(
  rootEntity: DocEntity,
  contextObj: any,
  descriptor: FkFieldDescriptor,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
  parentFieldPath?: string,
): void {
  const effectivePath = resolveFieldPath(parentFieldPath, descriptor.fieldPath);

  switch (descriptor.fkKind) {
    case "FOREIGN_KEY":
    case "FOREIGN_KEY_PATH":
      extractForeignKeyReference(rootEntity, contextObj, descriptor, effectivePath, referenceMap, foreignKeyReferences);
      break;
    case "FOREIGN_KEY_TABLE":
      extractForeignKeyTableReferences(
        rootEntity,
        contextObj,
        descriptor,
        effectivePath,
        referenceMap,
        foreignKeyReferences,
      );
      break;
    case "FOREIGN_KEY_STRING":
      extractForeignKeyStringReference(
        rootEntity,
        contextObj,
        descriptor,
        effectivePath,
        referenceMap,
        foreignKeyReferences,
      );
      break;
    case "FOREIGN_KEY_HASH_TABLE":
    case "FOREIGN_KEY_HASH_JOIN_TABLE":
      extractForeignKeyHashTableReferences(
        rootEntity,
        contextObj,
        descriptor,
        effectivePath,
        referenceMap,
        foreignKeyReferences,
      );
      break;
  }
}

/**
 * Combines a parent container path with a nested descriptor's relative path.
 * If both are non-empty, joins with ".". If either is empty, returns the other.
 */
function resolveFieldPath(parentFieldPath: string | undefined, descriptorPath: string): string {
  if (!parentFieldPath) {
    return descriptorPath;
  }
  if (!descriptorPath) {
    return parentFieldPath;
  }
  return `${parentFieldPath}.${descriptorPath}`;
}

/**
 * Extracts a single FOREIGN_KEY reference from a direct field value.
 * If the descriptor has nestedValueGroups (e.g., an array container),
 * iterates each element and applies the matching group's sub-descriptors.
 */
function extractForeignKeyReference(
  rootEntity: DocEntity,
  contextObj: any,
  descriptor: FkFieldDescriptor,
  effectivePath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const value = readFieldValueByParts(contextObj, descriptor.fieldPathParts);

  if (descriptor.referencedEntityTypes.length > 0 && isNonEmptyString(value)) {
    // Skip values that match a literal in the same union — these are system IDs,
    // not foreign key references (the SchemaValidator would match the literal branch first).
    if (!descriptor.literalExcludes?.has(value)) {
      for (const entityType of descriptor.referencedEntityTypes) {
        if (!docForeignKeyFilter(value, entityType)) {
          continue;
        }

        addDeduplicatedReference(
          effectivePath,
          rootEntity,
          entityType as DocEntityType,
          value,
          referenceMap,
          foreignKeyReferences,
        );
      }
    }
  }

  // Struct array elements get indexed paths; bare id arrays keep the unindexed path.
  if (descriptor.nestedValueGroups && Array.isArray(value)) {
    for (const [index, element] of value.entries()) {
      if (element !== null && element !== undefined) {
        const elementPath =
          typeof element === "object" ? resolveFieldPath(effectivePath, String(index)) : effectivePath;
        applyMatchingNestedGroups(
          rootEntity,
          element,
          descriptor.nestedValueGroups,
          elementPath,
          referenceMap,
          foreignKeyReferences,
        );
      }
    }
  }
}

/**
 * Extracts FOREIGN_KEY_TABLE references from the keys of an object field.
 * Also iterates each value to apply matching nested descriptor groups.
 */
function extractForeignKeyTableReferences(
  rootEntity: DocEntity,
  contextObj: any,
  descriptor: FkFieldDescriptor,
  effectivePath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const value = readFieldValueByParts(contextObj, descriptor.fieldPathParts);

  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return;
  }

  for (const key in value) {
    const keyedPath = resolveFieldPath(effectivePath, key);

    if (isNonEmptyString(key)) {
      for (const entityType of descriptor.referencedEntityTypes) {
        if (!docForeignKeyFilter(key, entityType)) {
          continue;
        }

        addDeduplicatedReference(
          keyedPath,
          rootEntity,
          entityType as DocEntityType,
          key,
          referenceMap,
          foreignKeyReferences,
        );
      }
    }

    // Apply matching nested descriptor groups to each table value
    if (descriptor.nestedValueGroups) {
      const tableValue = value[key];
      if (tableValue !== null && tableValue !== undefined) {
        applyMatchingNestedGroups(
          rootEntity,
          tableValue,
          descriptor.nestedValueGroups,
          keyedPath,
          referenceMap,
          foreignKeyReferences,
        );
      }
    }
  }
}

/**
 * Extracts FOREIGN_KEY_HASH_TABLE references from the values of a hash table field.
 * Unlike FOREIGN_KEY_TABLE where keys are entity IDs, hash table keys are handle hashes
 * that cannot be directly resolved without Doc context. References are extracted from
 * values only, via nested descriptor groups.
 */
function extractForeignKeyHashTableReferences(
  rootEntity: DocEntity,
  contextObj: any,
  descriptor: FkFieldDescriptor,
  effectivePath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const value = readFieldValueByParts(contextObj, descriptor.fieldPathParts);

  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return;
  }

  for (const key in value) {
    const keyedPath = resolveFieldPath(effectivePath, key);

    if (descriptor.nestedValueGroups) {
      const tableValue = value[key];
      if (tableValue !== null && tableValue !== undefined) {
        applyMatchingNestedGroups(
          rootEntity,
          tableValue,
          descriptor.nestedValueGroups,
          keyedPath,
          referenceMap,
          foreignKeyReferences,
        );
      }
    }
  }
}

interface NestedGroupLookup {
  readonly undiscriminated: readonly NestedValueDescriptorGroup[];
  readonly discriminated: readonly NestedValueDescriptorGroup[];
  readonly byValue: ReadonlyMap<string | number | boolean, NestedValueDescriptorGroup>;
  readonly discriminatorField: string | undefined;
}

const nestedGroupLookupCache = new WeakMap<readonly NestedValueDescriptorGroup[], NestedGroupLookup>();

function getNestedGroupLookup(groups: readonly NestedValueDescriptorGroup[]): NestedGroupLookup {
  const cached = nestedGroupLookupCache.get(groups);
  if (cached) {
    return cached;
  }

  const undiscriminated: NestedValueDescriptorGroup[] = [];
  const discriminated: NestedValueDescriptorGroup[] = [];
  const byValue = new Map<string | number | boolean, NestedValueDescriptorGroup>();
  let discriminatorField: string | undefined;

  for (const group of groups) {
    if (!group.discriminator) {
      undiscriminated.push(group);
      continue;
    }

    discriminated.push(group);
    discriminatorField = group.discriminator.field;
    byValue.set(group.discriminator.value, group);
  }

  const lookup: NestedGroupLookup = {
    undiscriminated,
    discriminated,
    byValue,
    discriminatorField,
  };
  nestedGroupLookupCache.set(groups, lookup);
  return lookup;
}

function applyNestedDescriptors(
  rootEntity: DocEntity,
  valueObj: any,
  group: NestedValueDescriptorGroup,
  parentFieldPath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  for (const nested of group.descriptors) {
    applyDescriptor(rootEntity, valueObj, nested, referenceMap, foreignKeyReferences, parentFieldPath);
  }
}

/**
 * Given a value object and a set of discriminator-aware nested descriptor groups,
 * applies only the descriptors from groups whose discriminator matches the value.
 * Groups without a discriminator always apply.
 */
function applyMatchingNestedGroups(
  rootEntity: DocEntity,
  valueObj: any,
  groups: readonly NestedValueDescriptorGroup[],
  parentFieldPath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const lookup = getNestedGroupLookup(groups);

  for (const group of lookup.undiscriminated) {
    applyNestedDescriptors(rootEntity, valueObj, group, parentFieldPath, referenceMap, foreignKeyReferences);
  }

  if (lookup.discriminated.length === 0 || lookup.discriminatorField === undefined) {
    return;
  }

  if (objectHasOwnField(valueObj, lookup.discriminatorField)) {
    const matched = lookup.byValue.get(valueObj[lookup.discriminatorField]);
    if (matched) {
      applyNestedDescriptors(rootEntity, valueObj, matched, parentFieldPath, referenceMap, foreignKeyReferences);
    }
    return;
  }

  // Fallback for partial container values that omit discriminator fields.
  // In that case, apply all discriminated groups and rely on field presence checks.
  for (const group of lookup.discriminated) {
    applyNestedDescriptors(rootEntity, valueObj, group, parentFieldPath, referenceMap, foreignKeyReferences);
  }
}

/**
 * Walks a field path once, capturing any Formula ancestor and the leaf value.
 */
function readPathForForeignKeyString(
  obj: unknown,
  parts: readonly string[],
): { formula: Formula | undefined; fieldValue: unknown } {
  if (parts.length === 0) {
    return {
      formula: isFormula(obj) ? obj : undefined,
      fieldValue: obj,
    };
  }

  let current: unknown = obj;
  let formula: Formula | undefined;

  for (const key of parts) {
    if (current === null || current === undefined || typeof current !== "object") {
      return { formula, fieldValue: undefined };
    }

    if (isFormula(current)) {
      formula = current;
    }

    current = (current as Record<string, unknown>)[key];
  }

  return { formula, fieldValue: current };
}

/**
 * Extracts FOREIGN_KEY_STRING references from formula or parameter-token string fields.
 * The candidate types on the descriptor determine whether we parse for option sets/options
 * or parameter references.
 */
function extractForeignKeyStringReference(
  rootEntity: DocEntity,
  contextObj: any,
  descriptor: FkFieldDescriptor,
  effectivePath: string,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const { referencedEntityTypes: candidateTypes } = descriptor;
  const { formula, fieldValue } = readPathForForeignKeyString(contextObj, descriptor.fieldPathParts);

  if (formula) {
    if (candidateTypes.includes("OPTION_SET") || candidateTypes.includes("ELEVATION")) {
      extractFormulaOptionReferences(effectivePath, rootEntity, formula, referenceMap, foreignKeyReferences);
      return;
    }

    extractFormulaParameterReferences(
      effectivePath,
      rootEntity,
      formula,
      candidateTypes,
      referenceMap,
      foreignKeyReferences,
    );

    // Parameter formulas can reference ActiveOptionState system parameters, whose ids are the
    // underlying option set ids. Also extract OPTION_SET/OPTION refs so doc-ref dependency
    // traversal can pull the catalog entities (SYSTEM_PARAMETER itself is doc-ref ineligible).
    extractFormulaOptionReferences(effectivePath, rootEntity, formula, referenceMap, foreignKeyReferences);
    return;
  }

  // Not a formula -- check for inline parameter tokens in the string value
  if (typeof fieldValue !== "string") {
    return;
  }

  if (stringContainsParameterTokens(fieldValue)) {
    const parameterIds = collectParameterIdsInString(fieldValue);

    for (const paramId in parameterIds) {
      // Annotation/text tokens can embed ActiveOptionState params whose ids are option set ids
      // (e.g. `{{ optionSet_xxx }}`). Prefer OPTION_SET so doc-ref can pull the catalog entity;
      // SYSTEM_PARAMETER is doc-ref ineligible and would otherwise win handle resolution.
      if (isOptionSetId(paramId)) {
        addDeduplicatedReference(effectivePath, rootEntity, "OPTION_SET", paramId, referenceMap, foreignKeyReferences);
        continue;
      }

      for (const paramType of parameterTypes) {
        addDeduplicatedReference(effectivePath, rootEntity, paramType, paramId, referenceMap, foreignKeyReferences);
      }
    }
  }
}

/**
 * Extracts option set / option references from a formula expression.
 */
function extractFormulaOptionReferences(
  fieldPath: string,
  entity: DocEntity,
  formula: Formula,
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  const optionHandles = formulaOptionHandles(formula.formula);

  for (const [rawOptionSetId, options] of Object.entries(optionHandles)) {
    // Parameter formulas compile identifiers with DEFAULT_IDENTIFIER_PREFIX. formulaOptionHandles
    // may still return that prefix after stripping a leading "not ", so normalize before typing.
    const optionSetId = removeIdentifierPrefix(rawOptionSetId);

    if (optionSetId === SystemOptionSet.ELEVATION_STYLE) {
      for (const elevationId of options) {
        addDeduplicatedReference(fieldPath, entity, "ELEVATION", elevationId, referenceMap, foreignKeyReferences);
      }
      continue;
    }

    if (docForeignKeyFilter(optionSetId, "OPTION_SET")) {
      addDeduplicatedReference(fieldPath, entity, "OPTION_SET", optionSetId, referenceMap, foreignKeyReferences);
    } else if (rawOptionSetId.startsWith(DEFAULT_IDENTIFIER_PREFIX)) {
      // Prefixed non-option-set identifiers (e.g. local parameters in Formula-typed fields)
      // resolve via full doc scan.
      addDeduplicatedReference(
        fieldPath,
        entity,
        "INDETERMINATE_ENTITY_TYPE",
        optionSetId,
        referenceMap,
        foreignKeyReferences,
      );
      continue;
    }

    for (const optionId of options) {
      if (docForeignKeyFilter(optionId, "OPTION")) {
        addDeduplicatedReference(fieldPath, entity, "OPTION", optionId, referenceMap, foreignKeyReferences);
      }
    }
  }
}

/**
 * Extracts parameter references from a formula expression.
 * Tries each parameter type that is present in the descriptor's candidate types.
 */
function extractFormulaParameterReferences(
  fieldPath: string,
  entity: DocEntity,
  formula: Formula,
  candidateTypes: readonly string[],
  referenceMap: Map<string, DocEntityReference>,
  foreignKeyReferences: DocEntityReference[],
): void {
  let matchingParamTypes: DocEntityType[] | undefined;

  for (const paramType of parameterTypes) {
    if (!candidateTypes.includes(paramType)) {
      continue;
    }
    if (!matchingParamTypes) {
      matchingParamTypes = [];
    }
    matchingParamTypes.push(paramType);
  }

  if (!matchingParamTypes) {
    return;
  }

  const parameterIds = formulaGetIdentifiers(formula);

  for (const rawId of parameterIds) {
    const paramId = removeIdentifierPrefix(rawId);

    // ActiveOptionState system parameters reuse the option set id as their parameter id.
    // Skip parameter-typed refs here so OPTION_SET from option extraction wins during handle
    // resolution; otherwise SYSTEM_PARAMETER would win the candidate race and be dropped as
    // doc-ref ineligible.
    if (isOptionSetId(paramId)) {
      continue;
    }

    for (const paramType of matchingParamTypes) {
      addDeduplicatedReference(fieldPath, entity, paramType, paramId, referenceMap, foreignKeyReferences);
    }
  }
}

/**
 * Filters FK values by entity type. OPTION_SET values must pass isOptionSetId,
 * OPTION values must pass isOptionId, all others pass through.
 */
function docForeignKeyFilter(fk: string | undefined, type: string | string[] | undefined): boolean {
  const typeStr = typeof type === "string" ? type : undefined;

  if (typeof fk !== "string" || fk === "" || !typeStr) {
    return true;
  }

  if (!isDocEntityType(typeStr)) {
    return true;
  }

  switch (typeStr) {
    case "OPTION_SET":
      return isOptionSetId(fk);
    case "OPTION":
      return isOptionId(fk);
    default:
      return true;
  }
}

interface ForeignKeyReferenceGroup extends Omit<DocEntityReference, "referencedEntityType"> {
  candidateTypes: Set<DocEntityType | CatalogItemType>;
  /** All distinct fieldPaths referencing this id; invalid refs report each path once. */
  fieldPaths: Set<string>;
}

export function buildForeignKeyReferenceGroups(refs: DocEntityReference[]): Map<string, ForeignKeyReferenceGroup> {
  const groups: Map<string, ForeignKeyReferenceGroup> = new Map();
  for (const { fieldPath, referencedEntityId, referencedEntityType, ...additionalMetadata } of refs) {
    if (!referencedEntityId) {
      continue;
    }

    let group = groups.get(referencedEntityId);
    if (!group) {
      group = { fieldPath, referencedEntityId, candidateTypes: new Set(), fieldPaths: new Set() };

      for (const key of Object.keys(additionalMetadata)) {
        const metadata = additionalMetadata[key];
        assertIsNotUndefined(group);

        if (metadata) {
          group[key] = metadata;
        }
      }

      groups.set(referencedEntityId, group);
    }

    group.fieldPaths.add(fieldPath);

    if (Array.isArray(referencedEntityType)) {
      for (const type of referencedEntityType) {
        group.candidateTypes.add(type);
      }
    } else {
      group.candidateTypes.add(referencedEntityType);
    }
  }
  return groups;
}
